import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
  Grid,
  Tooltip,
  Alert,
  Slide,
  Fade,
} from '@mui/material';
import {
  Label as LabelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ColorLens as ColorIcon,
} from '@mui/icons-material';
import { useIsMobile } from '../../hooks/useResponsive';
import LabelStorage from '../../utils/labelStorage';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Label Manager Component
 * Allows users to add, edit, and remove labels for locations
 */
const LabelManager = ({ locationId, locationName, onLabelsChange }) => {
  const isMobile = useIsMobile();
  const [labels, setLabels] = useState([]);
  const [presetLabels, setPresetLabels] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#2196f3');
  const [error, setError] = useState('');

  // Predefined colors for labels
  const colorPalette = [
    '#f44336', // Red
    '#e91e63', // Pink
    '#9c27b0', // Purple
    '#673ab7', // Deep Purple
    '#3f51b5', // Indigo
    '#2196f3', // Blue
    '#03a9f4', // Light Blue
    '#00bcd4', // Cyan
    '#009688', // Teal
    '#4caf50', // Green
    '#8bc34a', // Light Green
    '#cddc39', // Lime
    '#ffeb3b', // Yellow
    '#ffc107', // Amber
    '#ff9800', // Orange
    '#ff5722', // Deep Orange
    '#795548', // Brown
    '#9e9e9e', // Grey
    '#607d8b', // Blue Grey
  ];

  // Load labels when component mounts or locationId changes
  useEffect(() => {
    if (locationId) {
      loadLabels();
    }
    loadPresetLabels();
  }, [locationId]);

  const loadLabels = () => {
    const locationLabels = LabelStorage.getLabelsForLocation(locationId);
    setLabels(locationLabels);
  };

  const loadPresetLabels = () => {
    const presets = LabelStorage.getPresetLabels();
    setPresetLabels(presets);
  };

  const handleOpenDialog = (label = null) => {
    setEditingLabel(label);
    setNewLabelName(label?.name || '');
    setNewLabelColor(label?.color || '#2196f3');
    setError('');
    setDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLabel(null);
    setNewLabelName('');
    setNewLabelColor('#2196f3');
    setError('');
  };

  const handleSaveLabel = () => {
    if (!newLabelName.trim()) {
      setError('Label name is required');
      return;
    }

    try {
      if (editingLabel) {
        // Update existing label
        LabelStorage.updateLabel(locationId, editingLabel.id, {
          name: newLabelName.trim(),
          color: newLabelColor,
        });
      } else {
        // Add new label
        LabelStorage.addLabel(locationId, {
          name: newLabelName.trim(),
          color: newLabelColor,
        });
      }

      loadLabels();
      handleCloseDialog();

      // Notify parent component
      if (onLabelsChange) {
        onLabelsChange();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteLabel = (labelId) => {
    LabelStorage.removeLabel(locationId, labelId);
    loadLabels();

    // Notify parent component
    if (onLabelsChange) {
      onLabelsChange();
    }
  };

  const handleApplyPreset = (preset) => {
    try {
      LabelStorage.addLabel(locationId, {
        name: preset.name,
        color: preset.color,
      });

      loadLabels();
      setMenuAnchor(null);

      // Notify parent component
      if (onLabelsChange) {
        onLabelsChange();
      }
    } catch (err) {
      // Label already exists, just close menu
      setMenuAnchor(null);
    }
  };

  return (
    <Box>
      {/* Label chips */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
        {labels.map(label => (
          <Chip
            key={label.id}
            label={label.name}
            size="small"
            onDelete={() => handleDeleteLabel(label.id)}
            onClick={() => handleOpenDialog(label)}
            sx={{
              bgcolor: label.color,
              color: '#fff',
              '&:hover': {
                opacity: 0.8,
              },
              '& .MuiChip-deleteIcon': {
                color: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  color: '#fff',
                },
              },
            }}
            icon={<LabelIcon sx={{ color: '#fff !important' }} />}
          />
        ))}

        {/* Add label button */}
        <Tooltip title="Add label">
          <IconButton
            size={isMobile ? "medium" : "small"}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              minWidth: 44,
              minHeight: 44,
              width: { xs: 44, sm: 36, md: 32 },
              height: { xs: 44, sm: 36, md: 32 },
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
            }}
          >
            <AddIcon fontSize={isMobile ? "medium" : "small"} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Preset labels menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: { maxWidth: 300 },
        }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Quick labels
          </Typography>
        </MenuItem>
        {presetLabels.map(preset => (
          <MenuItem
            key={preset.id}
            onClick={() => handleApplyPreset(preset)}
            sx={{ gap: 1 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: preset.color,
              }}
            />
            <Typography variant="body2">{preset.name}</Typography>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => handleOpenDialog()}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">Custom label...</Typography>
        </MenuItem>
      </Menu>

      {/* Add/Edit label dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : Fade}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {editingLabel ? 'Edit Label' : 'Add Custom Label'}
            </Typography>
            {isMobile && (
              <IconButton
                onClick={handleCloseDialog}
                edge="end"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus={!isMobile}
            fullWidth
            label="Label Name"
            value={newLabelName}
            onChange={(e) => setNewLabelName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSaveLabel();
              }
            }}
            size={isMobile ? "medium" : "small"}
            sx={{ mt: 1, mb: 3 }}
          />

          <Typography variant="subtitle2" gutterBottom>
            Color
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 1 }} sx={{ mb: 2 }}>
            {colorPalette.map(color => (
              <Grid item key={color} xs={3} sm={2}>
                <IconButton
                  onClick={() => setNewLabelColor(color)}
                  sx={{
                    width: { xs: '100%', sm: 36, md: 32 },
                    height: { xs: 48, sm: 36, md: 32 },
                    minWidth: 44,
                    minHeight: 44,
                    bgcolor: color,
                    border: newLabelColor === color ? 3 : 0,
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: color,
                      opacity: 0.8,
                      transform: 'scale(1.1)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {newLabelColor === color && (
                    <CheckIcon sx={{ color: '#fff', fontSize: { xs: 24, sm: 18 } }} />
                  )}
                </IconButton>
              </Grid>
            ))}
          </Grid>

          {/* Preview */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={newLabelName || 'Label name'}
                size={isMobile ? "medium" : "small"}
                sx={{
                  bgcolor: newLabelColor,
                  color: '#fff',
                }}
                icon={<LabelIcon sx={{ color: '#fff !important' }} />}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {!isMobile && (
            <Button onClick={handleCloseDialog}>Cancel</Button>
          )}
          <Button
            onClick={handleSaveLabel}
            variant="contained"
            disabled={!newLabelName.trim()}
            fullWidth={isMobile}
            sx={{
              minHeight: { xs: 48, sm: 36 }
            }}
          >
            {editingLabel ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabelManager;
